import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Check, Info } from "lucide-react";
import {
  fetchSkinProfileCatalog,
  fetchSkinProfile,
  saveSkinProfile,
} from "../lib/api";

// Map backend concern.id → i18n key suffix. Backend ships English
// labels/descriptions; we override per-locale here. New concerns
// added to the catalog will fall back to the backend's English
// label until a translation lands — graceful, no UI breakage.
const CONCERN_I18N_MAP = {
  shine: "shine",
  dry_patches: "dryPatches",
  breakouts: "breakouts",
  fine_lines: "fineLines",
  redness: "redness",
  dark_spots: "darkSpots",
  dark_circles: "darkCircles",
  pores: "pores",
  texture: "texture",
  sensitivity: "sensitivity",
};

// Fallback catalog used if the /skin-profile/catalog fetch fails or
// times out. Keeps the onboarding flow moving — the user must be
// able to reach /capture even if the catalog endpoint blips. IDs
// mirror the backend `SKIN_CONCERNS` + `LIFE_STAGES` lists in
// /app/backend/skin_profile.py so the save call still validates.
// Labels/descriptions are translated at render time via
// CONCERN_I18N_MAP; the fallback English strings only surface if the
// backend catalog is unreachable AND the locale is untranslated.
const FALLBACK_CATALOG = {
  skin_concerns: [
    { id: "shine", label: "Shine throughout the day", description: "Skin gets oily by mid-afternoon." },
    { id: "dry_patches", label: "Dry patches or tightness", description: "Skin feels tight, sometimes flaky." },
    { id: "breakouts", label: "Breakouts come easily", description: "We'll lean toward non-comedogenic textures." },
    { id: "sensitivity", label: "Reactive to harsh products", description: "We'll keep formulas gentle and fragrance-light." },
    { id: "fine_lines", label: "Fine lines starting to show", description: "We'll favour lightweight, luminous formulas." },
    { id: "pores", label: "Visible pores", description: "We'll favour blurring + smoothing finishes." },
    { id: "redness", label: "Redness or uneven tone", description: "Color correction step gets prioritised." },
    { id: "dullness", label: "Dullness or lack of glow", description: "We'll lean radiant + lit-from-within." },
  ],
  life_stages: [
    { id: "under_30", label: "Under 30", description: "Skin is forgiving — most formulas wear well." },
    { id: "thirties", label: "30s", description: "Skin is finding its rhythm — we'll favour balanced finishes." },
    { id: "forties", label: "40s", description: "Skin starts to need lighter, more luminous formulas." },
    { id: "fifties_plus", label: "50s and beyond", description: "Lit-from-within wins — we'll skip heavy matte powders." },
    { id: "prefer_not_to_say", label: "I'd rather not say", description: "Totally fine — we'll use general defaults." },
  ],
};

/**
 * SkinProfilePicker — captures the user's skin concerns (multi-select)
 * and optional life stage (single-select). The saved values flow into
 * the recommendation engine to default coverage/finish and re-rank
 * products by skin-concern fit.
 *
 * Modes:
 *   - "settings"   — full editor with both sections + Save button
 *   - "onboarding" — same UI; parent receives onSaved(payload) and is
 *                    responsible for persistence
 *
 * We deliberately ask for "skin concerns" rather than "skin type" so
 * users don't feel pressure to self-diagnose. Most people know what
 * bothers them about their skin even if they'd never call themselves
 * "oily" or "combination."
 */
export default function SkinProfilePicker({
  mode = "settings",
  onSaved,
  onSkip,
  authed = true,
  initialSkinConcerns = null,
  initialLifeStage = null,
  // When true, suppress the server-side hydration that pre-fills the
  // picker with the user's saved skin profile. Used by Onboarding's
  // edit-mode ("Refresh my answers" path) so the user gets a clean
  // slate instead of having their old answers nudge them.
  skipHydration = false,
}) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState(null);
  const [skinConcerns, setSkinConcerns] = useState(
    () => new Set(initialSkinConcerns || []),
  );
  const [lifeStage, setLifeStage] = useState(initialLifeStage || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Belt-and-suspenders timeout: if the catalog fetch takes longer
    // than 3s, fall back to the in-memory catalog so the user is
    // never stuck on a spinner mid-onboarding.
    const fallbackTimer = setTimeout(() => {
      if (cancelled) return;
      setCatalog((cur) => cur || FALLBACK_CATALOG);
      setLoading(false);
    }, 3000);
    (async () => {
      try {
        const cat = await fetchSkinProfileCatalog();
        if (cancelled) return;
        setCatalog(cat);
        if (
          authed &&
          !skipHydration &&
          initialSkinConcerns === null &&
          initialLifeStage === null
        ) {
          try {
            const profile = await fetchSkinProfile();
            if (cancelled) return;
            setSkinConcerns(new Set(profile.skin_concerns || []));
            setLifeStage(profile.life_stage || null);
          } catch {
            /* unauthed → blank picker */
          }
        }
      } catch {
        // Catalog fetch failed — fall back to in-memory defaults so
        // the user can still complete the step. Never block onboarding
        // on a transient network blip.
        if (!cancelled) setCatalog((cur) => cur || FALLBACK_CATALOG);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
    };
  }, [authed, initialSkinConcerns, initialLifeStage, skipHydration]);

  const toggleConcern = (id) => {
    setSkinConcerns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!authed) {
      onSaved?.({
        skinConcerns: Array.from(skinConcerns),
        lifeStage,
      });
      return;
    }
    setSaving(true);
    try {
      const data = await saveSkinProfile(Array.from(skinConcerns), lifeStage);
      toast.success("Saved your skin profile.");
      onSaved?.({
        skinConcerns: data.skin_concerns,
        lifeStage: data.life_stage,
      });
    } catch {
      // Save failed (e.g. stealth/guest session or transient network
      // blip). Do NOT strand the user on the final onboarding step —
      // fire onSaved with the in-memory selection so the flow
      // continues to /capture. The next authenticated save from
      // Settings will still persist the choice server-side.
      toast.error("Couldn't save. Try again in a sec.");
      onSaved?.({
        skinConcerns: Array.from(skinConcerns),
        lifeStage,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-[#9B6A6C] flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }
  if (!catalog) return null;

  return (
    <div data-testid="skin-profile-picker">
      {/* ── Skin concerns (multi-select) ──────────────────────────── */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-[#2C2123] mb-1">
          {t("onboarding.steps.skinConcernsTitle")}
        </h4>
        <p className="text-xs text-[#8A7D82] mb-3">
          {t("onboarding.steps.skinConcernsSubtitle")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {catalog.skin_concerns.map((s) => {
            const on = skinConcerns.has(s.id);
            // Try the i18n map first; fall back to backend label so
            // newly-added concerns don't crash the picker.
            const i18nKey = CONCERN_I18N_MAP[s.id];
            const label = i18nKey
              ? t(`onboarding.steps.skinConcerns.${i18nKey}`, s.label)
              : s.label;
            const description = i18nKey
              ? t(`onboarding.steps.skinConcerns.${i18nKey}Desc`, s.description)
              : s.description;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => toggleConcern(s.id)}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${
                  on
                    ? "bg-[#9B6A6C]/10 border-[#9B6A6C] text-[#5D4050]"
                    : "bg-white border-[#EADDD7] hover:border-[#9B6A6C]/50 text-[#2C2123]"
                }`}
                aria-pressed={on}
                data-testid={`skin-concern-toggle-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-0.5">
                  <span className="font-medium text-sm">{label}</span>
                  {on && (
                    <Check className="h-4 w-4 text-[#9B6A6C] shrink-0 mt-0.5" />
                  )}
                </div>
                <p className="text-xs text-[#8A7D82] leading-snug">
                  {description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Life stage (single-select, optional) ─────────────────── */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-[#2C2123] mb-1">
          Life stage <span className="text-[#8A7D82] font-normal">· optional</span>
        </h4>
        <div
          className="flex items-start gap-2 mb-3 p-3 bg-[#FDF6F3] border border-[#F2E8E9] rounded-xl text-xs text-[#5C4B51] leading-relaxed"
          data-testid="skin-profile-life-stage-disclaimer"
        >
          <Info className="h-4 w-4 text-[#9B6A6C] mt-0.5 shrink-0" />
          <div>
            <strong className="text-[#2C2123]">Why we ask:</strong> it helps
            us give a more accurate understanding of your skin needs. If
            you'd rather not say, we totally get it.
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {catalog.life_stages.map((s) => {
            const on = lifeStage === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setLifeStage(on ? null : s.id)}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${
                  on
                    ? "bg-[#9B6A6C]/10 border-[#9B6A6C] text-[#5D4050]"
                    : "bg-white border-[#EADDD7] hover:border-[#9B6A6C]/50 text-[#2C2123]"
                }`}
                aria-pressed={on}
                data-testid={`life-stage-toggle-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-0.5">
                  <span className="font-medium text-sm">{s.label}</span>
                  {on && (
                    <Check className="h-4 w-4 text-[#9B6A6C] shrink-0 mt-0.5" />
                  )}
                </div>
                <p className="text-xs text-[#8A7D82] leading-snug">
                  {s.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-5 flex-wrap">
        <div className="text-xs text-[#8A7D82]">
          {skinConcerns.size === 0 && !lifeStage
            ? "Nothing selected — defaults will apply."
            : `${skinConcerns.size} concern${skinConcerns.size === 1 ? "" : "s"}${
                lifeStage ? ` · ${lifeStage.replace(/_/g, " ")}` : ""
              }`}
        </div>
        <div className="flex items-center gap-2">
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-[#8A7D82] hover:text-[#2C2123] underline-offset-4 hover:underline transition-colors"
              data-testid="skin-profile-skip"
            >
              Skip for now
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-wait"
            data-testid="skin-profile-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "onboarding" ? "Continue" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
